import escapeHtml from 'escape-html';
import { Text } from 'slate';
import { jsx } from 'slate-hyperscript';

export const deserializeInitialValue = (initialValue) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(initialValue, 'text/html');

    // Désérialiser le document
    return deserialize(doc.body);
};

export const decodeHtmlEntities = (text) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(text, 'text/html');
    return doc.documentElement.textContent;
};

export const stringify = (node) => {
    if (node.children) {
        return node.children.map(stringify).join('');
    }
    return node.text;
};

export const serialize = (node) => {
    if (Text.isText(node)) {
        let string = escapeHtml(node.text);

        if (node.bold) {
            string = `<strong>${string}</strong>`;
        }
        if (node.italic) {
            string = `<em>${string}</em>`;
        }
        if (node.underline) {
            string = `<u>${string}</u>`;
        }

        return string;
    }

    const children = node.children.map((n) => serialize(n)).join('');

    let alignmentStyle = '';
    if (node.align) {
        alignmentStyle = ` style="text-align: ${node.align};"`;
    }

    switch (node.type) {
        case 'quote':
            return `<blockquote><p${alignmentStyle}>${children}</p></blockquote>`;
        case 'paragraph':
            return `<p${alignmentStyle}>${children}</p>`;
        case 'link':
            return `<a href="${escapeHtml(node.url)}">${children}</a>`;
        case 'heading-one':
            return `<h1${alignmentStyle}>${children}</h1>`;
        case 'heading-two':
            return `<h2${alignmentStyle}>${children}</h2>`;
        case 'heading-three':
            return `<h3${alignmentStyle}>${children}</h3>`;
        case 'heading-four':
            return `<h4${alignmentStyle}>${children}</h4>`;
        case 'heading-five':
            return `<h5${alignmentStyle}>${children}</h5>`;
        case 'numbered-list':
            return `<ol>${children}</ol>`;
        case 'bulleted-list':
            return `<ul>${children}</ul>`;
        case 'list-item':
            return `<li${alignmentStyle}>${children}</li>`;
        default:
            return children;
    }
};

export const deserialize = (el, markAttributes = {}) => {
    if (el.nodeType === Node.TEXT_NODE) {
        return jsx('text', markAttributes, el.textContent);
    } else if (el.nodeType !== Node.ELEMENT_NODE) {
        return null;
    }

    const nodeAttributes = { ...markAttributes };

    // Vérification des styles d'alignement
    const textAlign = el.style.textAlign;
    if (textAlign) {
        nodeAttributes.align = textAlign;
    }

    switch (el.nodeName) {
        case 'STRONG':
            nodeAttributes.bold = true;
            break;
        case 'EM':
            nodeAttributes.italic = true;
            break;
        case 'U':
            nodeAttributes.underline = true;
            break;
        default:
            break;
    }

    // Récupérer les enfants et les désérialiser
    const children = Array.from(el.childNodes)
        .map((node) => deserialize(node, nodeAttributes))
        .flat();

    if (children.length === 0) {
        children.push(jsx('text', nodeAttributes, ''));
    }

    switch (el.nodeName) {
        case 'BODY':
            return jsx('fragment', {}, children);
        case 'BR':
            return '\n';
        case 'BLOCKQUOTE':
            return jsx('element', { type: 'quote', align: nodeAttributes.align }, children);
        case 'P':
            return jsx('element', { type: 'paragraph', align: nodeAttributes.align }, children);
        case 'A':
            return jsx('element', { type: 'link', url: el.getAttribute('href') }, children);
        case 'H1':
            return jsx('element', { type: 'heading-one', align: nodeAttributes.align }, children);
        case 'H2':
            return jsx('element', { type: 'heading-two', align: nodeAttributes.align }, children);
        case 'H3':
            return jsx('element', { type: 'heading-three', align: nodeAttributes.align }, children);
        case 'H4':
            return jsx('element', { type: 'heading-four', align: nodeAttributes.align }, children);
        case 'H5':
            return jsx('element', { type: 'heading-five', align: nodeAttributes.align }, children);
        case 'OL':
            return jsx('element', { type: 'numbered-list' }, children);
        case 'UL':
            return jsx('element', { type: 'bulleted-list' }, children);
        case 'LI':
            return jsx('element', { type: 'list-item', align: nodeAttributes.align }, children);
        default:
            return children;
    }
};
